
import {
  defineComponent,
  useMeta,
  useContext,
  ref,
  computed,
  useStore,
} from '@nuxtjs/composition-api'

import LazyHydrate from 'vue-lazy-hydration'
import { Sets } from '~/assets/ts/items/dao/sets'
import { Items } from '~/assets/ts/items/dao/items'
import { Conf } from '~/assets/ts/items/dao/conf'
import { ItemView } from '~/assets/ts/items/itemView'
import HtmlButton from '~/components/html/HtmlButton.vue'
import HtmlBrandButton from '~/components/html/htmlBrandButton.vue'
import IconItemColor from '~/components/icon/iconItemColor.vue'
import Loading from '~/components/Loading.vue'
import ItemVideo3d from '~/components/item/itemVideo3d.vue'
import ItemSocialSharing from '~/components/item/itemSocialSharing.vue'
import ItemAdditionalItems from '~/components/item/itemAdditionalItems.vue'
import { ItemHistory } from '~/assets/ts/items/history'
import ItemHistoryList from '~/components/item/itemHistoryList.vue'
import { Wishlist } from '~/assets/ts/items/wishlist'
import ElementButton from '~/components/element/elementButton.vue'
import Breadcrumb from '~/components/Breadcrumb.vue'
import ItemGetInspired from '~/components/item/itemGetInspired.vue'
import ItemLinks from '~/components/item/itemLinks.vue'
import MetaTitle from '~/components/meta/MetaTitle.vue'
import { Pages } from '~/assets/ts/pages'
import { getCountryCode } from '~/assets/ts/Locale'
import { getVersion } from '~/assets/ts/Version'

type setsWithLinksStructure = {
  sets: string[]
}

const defaultItem = new ItemView({
  jewellery_type: '0',
  finegold: [],
  price: '0',
  itemnumber: '',
  fineness: '',
  height: '0',
  stone_quality: '0',
  picture_600: '/img/item/def_item.jpg',
  description: {
    global_stone: {
      quality_short: '',
      quality: '',
      text: '',
    },
    text_alloy: '',
    text_profile: '',
    stone_size: '',
    stone_quality: '',
  },
  colorVars: [],
  jewelleryVars: {
    sizes: [],
    qualities: [],
    pools: [],
    numbers: [],
    finishes: [],
    models: [],
    settings: [],
  },
  set_itemnumber1: '',
  set_itemnumber2: '',
  set_itemnumber3: '',
  set_id: '',
  item1_price: '',
  item2_price: '',
  item3_price: '',
})

export default defineComponent({
  head: {},
  components: {
    ItemLinks,
    ItemGetInspired,
    ElementButton,
    ItemHistoryList,
    ItemAdditionalItems,
    ItemSocialSharing,
    ItemVideo3d,
    Loading,
    IconItemColor,
    HtmlBrandButton,
    HtmlButton,
    LazyHydrate,
    Breadcrumb,
    MetaTitle,
  },
  setup() {
    const {
      app: { i18n, router, $content },
    } = useContext()

    const store = useStore()
    const country = getCountryCode()

    let pageRoute = '' as string
    let pageName = '' as string
    let pageStr = '' as string

    const pageTitle = ref('' as string)

    const PagesHelper = new Pages()
    pageRoute = PagesHelper.getPageRoute(router)
    pageName = PagesHelper.getPageName(pageRoute)
    pageStr = PagesHelper.getPageStr(pageName)

    const isMaintenanceVisible = ref(false)

    const onMaintenanceClose = () => {
      isMaintenanceVisible.value = false
    }

    const item = ref({
      slug: '',
      obj: defaultItem,
    })
    const editParameter = ref('')
    const setSeoLink = ref('' as string)

    const video3dSetId = ref('' as string)
    const fastDeliverySetId = ref('' as string)
    const curImage = ref('main' as string)
    const isSet = ref(false as boolean)
    const isOnWishlist = ref(false as boolean)
    const loadingSessions = ref(0)
    const imageGridData = ref({})
    const imageGridBaseSets = ref([] as string[])

    if (typeof router === 'object') {
      store.commit('loading/increase')
      if (
        router.currentRoute.params.set.match(
          /^[A-Za-z]{1,2}-[0-9]{4}-[0-9]{1,}/
        )
      ) {
        isSet.value = true
        new Sets()
          .get(country, i18n.locale, router.currentRoute.params.set)
          .then((data) => {
            item.value.slug = router.currentRoute.params.set
            item.value.obj = new ItemView(data as any)
            imageGridData.value = (data as any).image_grid

            pageTitle.value = i18n.t(pageStr + '.title', {
              set: item.value.obj.getItemnumber(),
            }) as string

            store.commit('loading/decrease', 2)

            $content('seo', 'setsWithLinks')
              .fetch()
              .then((data: setsWithLinksStructure) => {
                if (data.sets.includes(item.value.obj.getItemnumber())) {
                  const seoLinks: { [key: string]: string } = {
                    weddingRing: 'weddingRings',
                    engagementRing: 'engagementRings',
                    earing: 'diamondJewelryEarrings',
                    memoire: 'diamondJewelryEarrings',
                    collier: 'diamondJewelryColliers',
                    diamond: 'diamondJewelryDelicateJewels',
                    ring: 'diamondJewelryCollectionRings',
                    bracelet: 'diamondJewelryDelicateJewels',
                    chain: 'diamondJewelryDelicateJewels',
                  }
                  setSeoLink.value =
                    seoLinks[item.value.obj.getJewelleryType()] ||
                    'weddingRings'
                }
              })

            $content('products', '3dvideos')
              .fetch()
              .then((data: { sets: string[] }) => {
                const video3dSets: string[] = data.sets.filter((s) =>
                  s.includes(item.value.slug.substring(0, 6).toUpperCase())
                )

                if (video3dSets.length > 0) {
                  video3dSetId.value = video3dSets[0]
                }
              })

            $content('products', 'fastDelivery')
              .fetch()
              .then((data: { sets: string[] }) => {
                const fastDeliverySets: string[] = data.sets.filter((s) =>
                  s === item.value.slug.toUpperCase()
                )

                if (fastDeliverySets.length > 0) {
                  fastDeliverySetId.value = fastDeliverySets[0]
                }

                fastDeliverySetId.value = ''

              })

            /**
             * Image Grid
             */
            $content('products', 'imageGrid')
              .fetch()
              .then((data: { sets: string[] }) => {
                imageGridBaseSets.value = data.sets
              })

            new ItemHistory().add(item.value.slug)
            new Wishlist()
              .isOnWishlist(item.value.slug)
              .then((wishlistResult: any) => {
                isOnWishlist.value = wishlistResult
              })
          })
      } else {
        isSet.value = false
        const confObj: Conf = new Conf()

        new Items()
          .get(country, i18n.locale, router.currentRoute.params.set)
          .then((data) => {
            item.value.slug = router.currentRoute.params.set
            item.value.obj = new ItemView(data as any)
            imageGridData.value = (data as any).image_grid

            pageTitle.value = i18n.t(pageStr + '.title', {
              set: item.value.obj.getItemnumber(),
            }) as string

            store.commit('loading/decrease', 2)

              $content('products', 'fastDelivery')
              .fetch()
              .then((data: { sets: string[] }) => {
                const fastDeliverySets: string[] = data.sets.filter((s) =>
                  s.includes(item.value.obj.getSetId().substring(0, 6).toUpperCase())
                )

                const fastDeliveryFinesses = [
                  "G5", // Yellowgold 585
                  "W5", // Whitegold 585
                  "W9" // Platinum 950
                ]

                if (fastDeliverySets.length > 0 && fastDeliveryFinesses.includes(item.value.obj.getFineness().toUpperCase())) {
                  fastDeliverySetId.value = fastDeliverySets[0]
                }

                fastDeliverySetId.value = ''

              })

            $content('seo', 'setsWithLinks')
              .fetch()
              .then((data: setsWithLinksStructure) => {
                if (data.sets.includes(item.value.obj.getItemnumber())) {
                  const seoLinks: { [key: string]: string } = {
                    weddingRing: 'weddingRings',
                    engagementRing: 'engagementRings',
                    earing: 'diamondJewelryEarrings',
                    memoire: 'diamondJewelryEarrings',
                    collier: 'diamondJewelryColliers',
                    diamond: 'diamondJewelryDelicateJewels',
                    ring: 'diamondJewelryCollectionRings',
                    bracelet: 'diamondJewelryDelicateJewels',
                    chain: 'diamondJewelryDelicateJewels',
                  }
                  setSeoLink.value =
                    seoLinks[item.value.obj.getJewelleryType()] ||
                    'weddingRings'
                }
              })

            new ItemHistory().add(item.value.slug)
            new Wishlist()
              .isOnWishlist(item.value.slug)
              .then((wishlistResult: any) => {
                isOnWishlist.value = wishlistResult
              })

            /**
             * Conf
             */

            loadingSessions.value++

            confObj
              .getJewelleryNormalStones(
                country,
                i18n.locale,
                router.currentRoute.params.set
              )
              .then((normalStoneData: any) => {
                loadingSessions.value--
                item.value.obj.item.jewelleryVars.sizes = normalStoneData.sizes
                item.value.obj.item.jewelleryVars.qualities =
                  normalStoneData.qualities
                item.value.obj.item.jewelleryVars.numbers =
                  normalStoneData.numbers
                item.value.obj.item.jewelleryVars.pools = normalStoneData.pools
              })
              .catch(() => {
                loadingSessions.value--
              })

            loadingSessions.value++

            confObj
              .getFinishes(country, i18n.locale, router.currentRoute.params.set)
              .then((finishData: any) => {
                loadingSessions.value--
                item.value.obj.item.jewelleryVars.finishes = finishData
              })
              .catch(() => {
                loadingSessions.value--
              })

            loadingSessions.value++

            confObj
              .getModels(country, i18n.locale, router.currentRoute.params.set)
              .then((modelData: any) => {
                loadingSessions.value--
                item.value.obj.item.jewelleryVars.models = modelData
              })
              .catch(() => {
                loadingSessions.value--
              })

            loadingSessions.value++

            confObj
              .getSettings(country, i18n.locale, router.currentRoute.params.set)
              .then((data: any) => {
                loadingSessions.value--
                item.value.obj.item.jewelleryVars.settings = data
              })
              .catch(() => {
                loadingSessions.value--
              })
          })
      }
    }

    useMeta(() => {
      if (
        typeof item.value.obj.getItemnumber === 'function' &&
        item.value.obj.getItemnumber() !== ''
      ) {
        return {
          title: pageTitle.value as string,
          meta: [
            {
              hid: 'description',
              name: 'description',
              content: i18n.t(pageStr + '.description', {
                set: item.value.obj.getItemnumber(),
              }) as string,
            },
            {
              hid: 'og:title',
              property: 'og:title',
              content: i18n.t(pageStr + '.title', {
                set: item.value.obj.getItemnumber(),
              }) as string,
            },
            {
              hid: 'og:description',
              property: 'og:description',
              content: i18n.t(pageStr + '.description', {
                set: item.value.obj.getItemnumber(),
              }) as string,
            },
            {
              hid: 'og:image',
              property: 'og:image',
              content: item.value.obj.getImage(),
            },
          ],
        }
      } else {
        return {}
      }
    })

    const shoppingCartUrl = computed((): string => {
      if (process.env.NUXT_ENV_DEFAULT_CODE === 'de') {
        return 'https://www.123gold.de/konfigurator/'
      } else if (process.env.NUXT_ENV_DEFAULT_CODE === 'at') {
        return 'https://www.123gold.at/konfigurator/'
      } else if (process.env.NUXT_ENV_DEFAULT_CODE === 'ch') {
        return 'https://www.123gold.ch/konfigurator/'
      } else if (process.env.NUXT_ENV_DEFAULT_CODE === 'nl') {
        return 'https://www.123gold.nl/configurator/'
      } else {
        return ''
      }
    })

    const toggleWishlist = () => {
      if (!isOnWishlist.value) {
        new Wishlist().add(item.value.slug).then(() => {
          isOnWishlist.value = true
          store.commit('wishlist/increase')
        })
      } else {
        new Wishlist().remove(item.value.slug).then(() => {
          isOnWishlist.value = false
          store.commit('wishlist/decrease')
        })
      }
    }

    return {
      pageRoute,
      pageStr,
      pageName,
      pageTitle,
      i18n,
      isMaintenanceVisible,
      onMaintenanceClose,
      item,
      editParameter,
      shoppingCartUrl,
      setSeoLink,
      video3dSetId,
      fastDeliverySetId,
      curImage,
      isSet,
      isOnWishlist,
      toggleWishlist,
      country,
      loadingSessions,
      imageGridData,
      imageGridBaseSets,
      getVersion,
    }
  },
})
